import { useState, useEffect, useLayoutEffect, useCallback, useRef, ReactNode } from 'react';
import { AuthContext, AuthUser, ClinicInfo } from './useAuth';
import { apiFetch, setApiClinic, setSessionExpiredHandler, setIpRefusedHandler, AUTH_ME_PATH } from '../lib/api';
import { NetworkRefusedPage } from './NetworkRefusedPage';
import { setWaitsSignedIn } from '../lib/wait-timing';

const CLINIC_STORAGE_KEY = 'hq_selected_clinic';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [clinics, setClinics] = useState<ClinicInfo[]>([]);
  const [selectedClinic, setSelectedClinicState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // The owl's wait timing posts only while signed in. A layout effect runs
  // before the passive cleanups where an unmounting owl reports, so a page
  // torn down by a sign-out never posts.
  useLayoutEffect(() => setWaitsSignedIn(!!user), [user]);
  // Set when the clinic's staff IP allowlist refuses a request: the app is
  // replaced by the network page, which (like <LoginPage>) unmounts every
  // module and so stops all polling. Holds the refused address.
  const [ipRefused, setIpRefused] = useState<{ ip: string | null } | null>(null);

  const checkAuth = useCallback(async () => {
    try {
      // ⚠️ `/api/auth/me` answers **200 `{authenticated: false}`** when logged
      // out — it does NOT 401 (see `auth/index.ts`). So "am I signed in?" is a
      // question about the PAYLOAD, not the status. Both fields are absent in
      // that body; defaulting them here makes the logged-out case take this
      // success path deterministically instead of reaching the catch via a
      // TypeError on `data.clinics.some`.
      const data = await apiFetch<{ user?: AuthUser; clinics?: ClinicInfo[] }>(AUTH_ME_PATH);
      const clinics = data.clinics ?? [];
      setUser(data.user ?? null);
      setClinics(clinics);

      // Restore selected clinic from localStorage, or default to first
      const stored = localStorage.getItem(CLINIC_STORAGE_KEY);
      const validStored = stored && clinics.some((c) => c.clientId === stored);
      if (validStored) {
        setSelectedClinicState(stored);
        setApiClinic(stored);
      } else if (clinics.length > 0) {
        const defaultClinic = clinics[0].clientId;
        setSelectedClinicState(defaultClinic);
        setApiClinic(defaultClinic);
        localStorage.setItem(CLINIC_STORAGE_KEY, defaultClinic);
      }
    } catch {
      setUser(null);
      setClinics([]);
      setSelectedClinicState(null);
    }
  }, []);

  useEffect(() => {
    checkAuth().finally(() => setLoading(false));
  }, [checkAuth]);

  // Clearing `user` makes App render <LoginPage>, which unmounts every module —
  // and with them every polling timer, via their existing effect cleanups. That
  // is the whole mechanism: one place to stop ALL zombie polling, not a
  // per-page fix. Plan: docs/plans/2026-07-31-prod-log-hygiene-and-timeouts.md.
  const loadingRef = useRef(loading);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const verifyingRef = useRef(false);
  useEffect(
    () =>
      setSessionExpiredHandler(() => {
        // Still doing the initial probe — its own result is about to settle
        // this anyway, and reacting here would stomp an in-flight redirect.
        if (loadingRef.current) return;
        // A burst of concurrent 401s (a page with several parallel fetches)
        // must produce ONE re-check, and that re-check must not re-enter.
        if (verifyingRef.current) return;
        verifyingRef.current = true;

        // CONFIRM before acting, because a 401 alone doesn't mean our session
        // died: `/api/kiosks/*` and `/api/documents/*` are proxied and forward
        // the UPSTREAM's 401 (document-manager's is even produced deliberately
        // when DOCUMENT_MANAGER_SECRET is unset). Logging an admin out because
        // a downstream service is misconfigured would be worse than the bug
        // this fixes.
        //
        // `checkAuth` IS the confirmation — and it must be, because
        // `/api/auth/me` answers 200 `{authenticated:false}` when logged out
        // rather than 401. A bespoke probe that only inspected rejection
        // therefore never fired (caught by e2e on the first staging deploy).
        // checkAuth reads the payload: real user → state refreshes and nothing
        // else happens; logged out → `user` clears → <LoginPage>.
        void checkAuth().finally(() => {
          verifyingRef.current = false;
        });
      }),
    [checkAuth],
  );

  useEffect(() => setIpRefusedHandler((ip) => setIpRefused({ ip })), []);

  const login = useCallback(
    async (username: string, password: string) => {
      const data = await apiFetch<Record<string, unknown>>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      if (data.requires2FA) return data;
      await checkAuth();
      return data;
    },
    [checkAuth],
  );

  const verifyOtp = useCallback(
    async (code: string) => {
      await apiFetch('/api/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      await checkAuth();
    },
    [checkAuth],
  );

  const logout = useCallback(async () => {
    await apiFetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
    setClinics([]);
    setSelectedClinicState(null);
    setIpRefused(null);
    localStorage.removeItem(CLINIC_STORAGE_KEY);
  }, []);

  const setSelectedClinic = useCallback((id: string) => {
    setSelectedClinicState(id);
    setApiClinic(id);
    localStorage.setItem(CLINIC_STORAGE_KEY, id);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, clinics, selectedClinic, setSelectedClinic, login, logout, verifyOtp, refreshUser: checkAuth, loading }}
    >
      {ipRefused ? <NetworkRefusedPage ip={ipRefused.ip} onSignOut={logout} /> : children}
    </AuthContext.Provider>
  );
}
